import { z } from 'zod';

// Enhanced password validation based on security best practices
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters long')
  .max(128, 'Password must be less than 128 characters')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one special character');

// More lenient password schema for legacy users or simplified signup
export const basicPasswordSchema = z
  .string()
  .min(6, 'Password must be at least 6 characters long')
  .max(128, 'Password must be less than 128 characters');

export interface PasswordStrength {
  score: number; // 0-4 scale
  feedback: string[];
  isValid: boolean;
}

export function calculatePasswordStrength(password: string): PasswordStrength {
  const feedback: string[] = [];
  let score = 0;

  // Length check
  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('Use at least 8 characters');
  }

  // Character variety checks
  if (/[a-z]/.test(password)) {
    score += 0.5;
  } else {
    feedback.push('Add lowercase letters');
  }

  if (/[A-Z]/.test(password)) {
    score += 0.5;
  } else {
    feedback.push('Add uppercase letters');
  }

  if (/[0-9]/.test(password)) {
    score += 0.5;
  } else {
    feedback.push('Add numbers');
  }

  if (/[^a-zA-Z0-9]/.test(password)) {
    score += 0.5;
  } else {
    feedback.push('Add special characters');
  }

  // Length bonus
  if (password.length >= 12) {
    score += 0.5;
  }

  if (password.length >= 16) {
    score += 0.5;
  }

  // Common patterns penalty
  if (/(.)\1{2,}/.test(password)) {
    score -= 0.5;
    feedback.push('Avoid repeating characters');
  }

  if (/123|abc|qwe|password|admin/i.test(password)) {
    score -= 1;
    feedback.push('Avoid common patterns');
  }

  // Normalize score to 0-4 range
  score = Math.max(0, Math.min(4, score));

  return {
    score: Math.round(score),
    feedback: feedback.slice(0, 3), // Limit to top 3 suggestions
    isValid: score >= 2 && password.length >= 6, // Minimum requirements
  };
}

export function getPasswordStrengthLabel(score: number): string {
  switch (score) {
    case 0:
    case 1:
      return 'Weak';
    case 2:
      return 'Fair';
    case 3:
      return 'Good';
    case 4:
      return 'Strong';
    default:
      return 'Unknown';
  }
}

export function getPasswordStrengthColor(score: number): string {
  switch (score) {
    case 0:
    case 1:
      return 'text-red-500';
    case 2:
      return 'text-yellow-500';
    case 3:
      return 'text-blue-500';
    case 4:
      return 'text-green-500';
    default:
      return 'text-gray-500';
  }
}

// Email validation with additional checks
export const enhancedEmailSchema = z
  .string()
  .email('Please enter a valid email address')
  .min(5, 'Email address is too short')
  .max(254, 'Email address is too long')
  .refine((email) => {
    // Additional validation for common issues
    const domain = email.split('@')[1];
    if (!domain) return false;
    
    // Check for valid domain structure
    return /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$/.test(domain);
  }, 'Please enter a valid email address');

export const signupFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[a-zA-Z\s\-'\.]+$/, 'Name can only contain letters, spaces, hyphens, apostrophes, and periods'),
  email: enhancedEmailSchema,
  password: basicPasswordSchema, // Using basic schema for now, can upgrade to passwordSchema later
});

export type SignupFormData = z.infer<typeof signupFormSchema>;